import React, { useState, useEffect, useRef } from "react";
import { BottomSheet } from "./BottomSheet.jsx";
import { Button } from "./Button.jsx";
import { NumberField } from "./NumberField.jsx";

const STARTING_ROWS = 5;
const emptyRows = (n) => Array.from({ length: n }, () => null);
const round3 = (n) => Math.round(n * 1000) / 1000;

const makeInitialRows = (initial) => {
  if (Array.isArray(initial) && initial.length > 0) {
    const valid = initial.filter((v) => typeof v === "number" && !isNaN(v) && v > 0);
    if (valid.length > 0) {
      const padding = Math.max(0, STARTING_ROWS - valid.length);
      return [...valid, ...emptyRows(padding)];
    }
  }
  return emptyRows(STARTING_ROWS);
};

/**
 * Helper popup: enter several partial quantities (bundles, bags, deliveries…),
 * see the running total, and apply the sum and breakdown to a line's quantity.
 */
export function QuantityCalculatorSheet({ isOpen, onClose, itemName, unit = "pcs", initialRows = null, onApply }) {
  const [rows, setRows] = useState(() => makeInitialRows(initialRows));
  const [focusIndex, setFocusIndex] = useState(null);
  const [session, setSession] = useState(0);
  const listRef = useRef(null);

  // Load existing breakdown or fresh rows whenever the sheet opens
  useEffect(() => {
    if (isOpen) {
      const initialized = makeInitialRows(initialRows);
      setRows(initialized);
      setSession((n) => n + 1);
      // Focus the first empty slot or row 0
      const firstEmpty = initialized.findIndex((v) => v === null || v === 0);
      setFocusIndex(firstEmpty !== -1 ? firstEmpty : initialized.length - 1);
    }
  }, [isOpen, initialRows]);

  const handleClear = () => {
    setRows(emptyRows(STARTING_ROWS));
    setFocusIndex(0);
  };

  // Move focus after rows render (e.g. Enter on the last row adds one)
  useEffect(() => {
    if (focusIndex === null || !listRef.current) return;
    const input = listRef.current.querySelector(`[data-qty-row="${focusIndex}"]`);
    if (input) {
      input.focus();
      setFocusIndex(null);
    }
  }, [focusIndex, rows]);

  const filled = rows.filter((v) => typeof v === "number" && v > 0);
  const total = round3(filled.reduce((sum, v) => sum + v, 0));

  const setRow = (index, value) => {
    setRows((prev) => prev.map((v, i) => (i === index ? value : v)));
  };

  const removeRow = (index) => {
    setRows((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : emptyRows(1);
    });
  };

  const goNext = (index) => {
    if (index === rows.length - 1) setRows((prev) => [...prev, null]);
    setFocusIndex(index + 1);
  };

  const handleApply = () => {
    if (total <= 0) return;
    onApply(total, filled);
    onClose();
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="🧮 Add up quantity">
      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        {itemName && (
          <div style={{ fontSize: "0.85rem", color: "var(--text-muted)", fontWeight: "600", marginTop: "-0.4rem" }}>
            {itemName}
          </div>
        )}

        <div ref={listRef} style={{ display: "flex", flexDirection: "column", gap: "0.45rem", maxHeight: "46vh", overflowY: "auto", paddingRight: "2px" }}>
          {rows.map((value, index) => (
            <div key={`${session}-${index}`} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span style={{ width: "1.8rem", textAlign: "right", fontSize: "0.8rem", color: "var(--text-dim)", fontWeight: "700" }}>
                {index + 1}.
              </span>
              <NumberField
                data-qty-row={index}
                aria-label={`Quantity ${index + 1}`}
                enterKeyHint="next"
                min={0}
                fallback={0}
                decimals={3}
                allowEmpty
                value={value}
                onValueChange={(v) => setRow(index, v)}
                onEnter={() => goNext(index)}
                placeholder="0"
                style={{ flex: 1, fontWeight: "700", fontSize: "1rem", textAlign: "right" }}
              />
              <span style={{ fontSize: "0.75rem", color: "var(--text-dim)", width: "2.2rem" }}>{unit}</span>
              <button
                type="button"
                className="btn-icon"
                onClick={() => removeRow(index)}
                aria-label={`Remove quantity ${index + 1}`}
                style={{ color: "var(--danger)" }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button
            type="button"
            onClick={() => {
              const firstNew = rows.length;
              setRows((prev) => [...prev, ...emptyRows(STARTING_ROWS)]);
              setFocusIndex(firstNew);
            }}
            style={{ alignSelf: "flex-start", background: "none", border: "none", color: "var(--primary)", fontWeight: "700", cursor: "pointer", padding: 0, fontSize: "0.85rem" }}
          >
            + Add more rows
          </button>
          {filled.length > 0 && (
            <button
              type="button"
              onClick={handleClear}
              style={{ background: "none", border: "none", color: "var(--danger)", fontWeight: "600", cursor: "pointer", padding: 0, fontSize: "0.82rem" }}
            >
              🗑️ Clear all
            </button>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", padding: "0.65rem 0.85rem" }}>
          <span style={{ fontSize: "1rem", fontWeight: "800", color: "var(--text-main)" }}>
            Total: {total.toLocaleString("en-IN", { maximumFractionDigits: 3 })} {unit}
          </span>
          <span style={{ fontSize: "0.78rem", color: "var(--text-dim)" }}>
            {filled.length} {filled.length === 1 ? "entry" : "entries"}
          </span>
        </div>

        <div style={{ display: "flex", gap: "0.75rem" }}>
          <Button type="button" variant="secondary" onClick={onClose} style={{ flex: 1 }}>
            Cancel
          </Button>
          <Button type="button" variant="primary" onClick={handleApply} disabled={total <= 0} style={{ flex: 1 }}>
            Apply {total > 0 ? total.toLocaleString("en-IN", { maximumFractionDigits: 3 }) : ""}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}

export default QuantityCalculatorSheet;
